import { Component, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'gains-input-action-cell',
  standalone: false,
  templateUrl: './gains-input-action-cell.component.html',
  styleUrls: ['./gains-input-action-cell.component.scss']
})
export class GainsInputActionCellComponent implements ICellRendererAngularComp, OnDestroy {
  params!: ICellRendererParams;
  private destroy$ = new Subject<void>();

  formControl = new FormControl('');
  isOptionalPanelEnabled = false;
  isFormSubmitted = false;
  inputPlaceholder = 'Same as default';

  get showValidationError(): boolean {
    return this.isFormSubmitted && this.formControl.invalid;
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.isOptionalPanelEnabled = params.data?.__panelEnabled === true && params.data?.enabled === 'Opt.';
    this.isFormSubmitted = params.data?.__formSubmitted === true;
    this.setInputPlaceholder();

    // Set initial value and validators
    this.updateFormControl();

    // Listen for value changes and sync with ag-Grid
    this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(updatedValue => {
      this.onValueChange(updatedValue ?? '');
    });
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.isOptionalPanelEnabled = params.data?.__panelEnabled === true && params.data?.enabled === 'Opt.';
    this.isFormSubmitted = params.data?.__formSubmitted === true;
    this.setInputPlaceholder();

    this.updateFormControl();
    return true;
  }

  private setInputPlaceholder(): void {
    this.inputPlaceholder = this.isOptionalPanelEnabled ? 'Required' : 'Same as default';
  }

  private updateFormControl(): void {
    const initialValue = `${this.params.value ?? ''}`;
    const validators = this.isOptionalPanelEnabled ? Validators.required : [];

    this.formControl.setValidators(validators);
    this.formControl.updateValueAndValidity({ emitEvent: false });
    this.formControl.setValue(initialValue, { emitEvent: false });
  }

  onValueChange(updatedValue: string): void {
    const field = this.params.colDef?.field;

    if (!field) {
      return;
    }

    this.params.node.setDataValue(field, updatedValue);

    const rowIndex = Number(this.params.data?.__sourceRowIndex ?? -1);
    this.params.context?.onInputValueChange?.(rowIndex, field, updatedValue);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
